import { editorialStatusBlock, metricsStrip, numberedSteps, button, hairlineRow, collapsible } from "../../design/components.js";
import { mountPaperMap, type MapHandle } from "./map-view.js";
import { safetyStepKeys } from "./safety-steps.js";
import type { IncidentView } from "./incident-model.js";
import type { SpreadView } from "./alert-model.js";
import type { WeatherView } from "./weather-model.js";
import { compass8 } from "../../lib/compass.js";
import { trendLabelKey, contributorLabels, technicalRows } from "./incident-presentation.js";
import { hazardIcon } from "./hazard-icons.js";
import type { RawReportPoint } from "./raw-report-model.js";
import { warnungBundUrl } from "./warning-link.js";

export interface IncidentDetailCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  view: IncidentView;
  spread?: SpreadView;
  weather?: WeatherView;
  distanceKm?: number;
  /** Home watch radius (km). With distanceKm it decides "near" (inside your zone) vs
   *  "away" (you're browsing it) — drives the headline wording + whether the evacuation
   *  Safety pane leads. Absent ⇒ treat as near (can't tell), preserving prior behaviour. */
  radiusKm?: number;
  placeName?: string;
  nowMs: number;
  onBack?: () => void;
  onHelp?: () => void;
  /** "I see it too" — corroborate this incident with my own report (D2 wiring). */
  onSeeItToo?: () => void | Promise<void>;
  onFlag?: (subject: string) => void;
  /** Raw reports falling in this incident's cells (power-user opt-in) — shown as a
   *  tappable list so you can reach the underlying observations behind the snapshot. */
  reports?: RawReportPoint[];
  onOpenReport?: (id: string) => void;
  /** Receives the mounted mini-map handle so the caller can `destroy()` it when the
   *  overlay closes — the detail map is rebuilt per open, so it must be torn down to
   *  avoid leaking a WebGL context. Not called when the incident has no cell. */
  onMapHandle?: (handle: MapHandle) => void;
}

const HAZARD_HEADLINE: Record<string, string> = {
  fire: "now.status.nearby.fire", flood: "now.status.nearby.flood",
  earthquake: "now.status.nearby.earthquake", conflict: "now.status.nearby.conflict",
  other: "now.status.nearby.other",
  traffic: "now.status.nearby.traffic", storm: "now.status.nearby.storm",
  snow: "now.status.nearby.snow", ice: "now.status.nearby.ice",
  heat: "now.status.nearby.heat", fog: "now.status.nearby.fog",
  hazmat: "now.status.nearby.hazmat", outage: "now.status.nearby.outage",
};

function relativeAgo(t: IncidentDetailCtx["t"], iso: string, nowMs: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return t("incident.detail.ago.now");
  const min = Math.max(0, Math.round((nowMs - then) / 60000));
  if (min < 1) return t("incident.detail.ago.now");
  if (min < 60) return t("incident.detail.ago.min", { n: min });
  return t("incident.detail.ago.hr", { n: Math.round(min / 60) });
}

function spreadLabel(t: IncidentDetailCtx["t"], s?: SpreadView): string {
  if (!s) return t("incident.detail.spread.unknown");
  if (s.status === "moving") return t("incident.detail.spread.moving", { speed: Math.round(s.speedKmh ?? 0) });
  if (s.status === "dissipating") return t("incident.detail.spread.dissipating");
  return t("incident.detail.spread.stationary");
}

/** "How bad" 1–3 from the projector confidence. A stand-in until app.aid.incident.snapshot
 *  carries an aggregated severity directly (matches now-tab's confidence→ticks proxy). */
function severityFromConfidence(c: number): number {
  return c >= 0.8 ? 3 : c >= 0.5 ? 2 : 1;
}

const KNOWN_STATUS = new Set(["open", "sustained", "closing", "closed"]);
function statusLabel(t: IncidentDetailCtx["t"], status: string): string {
  return KNOWN_STATUS.has(status) ? t(`incident.status.${status}`) : status;
}

/** IncidentDetail (GUI §3) — hero headline, mini paper map w/ spread cone, metrics row,
 *  per-hazard tone-invariant safety steps, footer actions. Tone slider (C6) styles only the
 *  headline; facts (distance, ×N, steps) are tone-invariant. */
export function incidentDetail(ctx: IncidentDetailCtx): HTMLElement {
  const { t, view } = ctx;
  const root = document.createElement("section");
  root.className = "screen incident-detail";
  root.setAttribute("data-incident-detail", view.subject);

  // Modal-style header: a ✕ close (top-right) rather than a back button, so the detail
  // reads as a dialog (matches the request screen). data-action stays "back" so the
  // back-button stack + tests are unaffected.
  const header = document.createElement("div");
  header.className = "incident-detail__header";
  const title = document.createElement("div");
  title.className = "bx-microlabel incident-detail__title";
  title.textContent = t(`hazard.${view.hazard}`).toUpperCase();
  const close = document.createElement("button");
  close.type = "button";
  close.className = "bx-modal__close incident-detail__close";
  close.setAttribute("aria-label", t("incident.detail.back"));
  close.setAttribute("data-action", "back");
  close.textContent = "✕";
  close.addEventListener("click", () => ctx.onBack?.());
  header.append(title, close);
  root.append(header);

  // Everything below the header scrolls; the header (with ✕) stays pinned.
  const body = document.createElement("div");
  body.className = "incident-detail__body";

  // Distance is measured to the nearest watched zone (home + saved + live), not just home.
  // Three tiers: inZone (≤ radius) reads "{hazard} nearby" with Safety leading; out to 2×
  // radius it reads "{hazard} — N km away" but KEEPS safety steps (a fire just outside your
  // zone can still reach you); beyond 2× radius the Safety pane is dropped. Unknown ⇒ inZone.
  const known = ctx.distanceKm != null && ctx.radiusKm != null;
  const inZone = !known || (ctx.distanceKm as number) <= (ctx.radiusKm as number);
  const showSafety = !known || (ctx.distanceKm as number) <= 2 * (ctx.radiusKm as number);
  const dist = ctx.distanceKm != null ? `${ctx.distanceKm.toFixed(1)} km` : "";
  body.append(editorialStatusBlock({
    microlabel: [t(`hazard.${view.hazard}`), dist].filter(Boolean).join(" · "),
    headline: inZone
      ? t(HAZARD_HEADLINE[view.hazard] ?? "now.status.nearby.other")
      : t("incident.detail.away", { hazard: t(`hazard.${view.hazard}`), n: Math.round(ctx.distanceKm as number) }),
    subline: ctx.placeName,
  }));

  // Mini paper map (WebGL-guarded; cone data wired, drawing deferred to D2).
  const mapMount = document.createElement("div");
  mapMount.className = "now-map detail-map";
  mapMount.setAttribute("data-map", "");
  if (view.cell) {
    const h = mountPaperMap(mapMount, { centerCell: view.cell });
    h.setIncidents([view], ctx.nowMs);
    if (ctx.spread) h.setSpreadCone(ctx.spread.pathCells);
    if (ctx.spread?.windConeCells?.length) h.setWindCone([{ cells: ctx.spread.windConeCells }]);
    ctx.onMapHandle?.(h); // hand the caller the handle so it can destroy() on close
  }
  body.append(mapMount);

  // --- Swipe carousel (GUI §3 panic-ergonomics): Safety FIRST, then Details, then
  //     Actions — so the dense step list and the metadata stop competing for the same
  //     vertical space. Horizontal scroll-snap = native swipe; the tabs jump + reflect. ---
  const buildSafety = (): HTMLElement => {
    const pane = document.createElement("div");
    pane.className = "detail-pane-body";
    const stepsLabel = document.createElement("div");
    stepsLabel.className = "bx-microlabel";
    stepsLabel.textContent = t("incident.detail.steps");
    pane.append(stepsLabel, numberedSteps(safetyStepKeys(view.hazard).map((k) => t(k)), -1));
    return pane;
  };

  const buildDetails = (): HTMLElement => {
    const pane = document.createElement("div");
    pane.className = "detail-pane-body";
    pane.append(metricsStrip([
      { value: t("incident.detail.metrics.corroborated", { n: view.distinctIdentities }), label: t("incident.detail.metrics.corroboratedLabel") },
      { value: relativeAgo(t, view.firstSeenIso ?? view.createdAtIso, ctx.nowMs), label: t("incident.detail.metrics.firstLabel") },
      { value: spreadLabel(t, ctx.spread), label: t("incident.detail.metrics.spreadLabel") },
    ]));
    // Origin (community vs official), how bad, status — the facts behind the headline.
    const details = document.createElement("div");
    details.className = "detail-meta";
    details.setAttribute("data-meta", "");
    const dTitle = document.createElement("div");
    dTitle.className = "bx-microlabel";
    dTitle.textContent = t("incident.detail.meta.title");
    details.append(dTitle);
    const origin = view.source === "authority" ? t("incident.detail.origin.authority") : t("incident.detail.origin.crowd");
    details.append(hairlineRow({ leading: t("incident.detail.meta.origin"), trailing: origin }));
    const sev = severityFromConfidence(view.confidence);
    details.append(hairlineRow({ leading: t("incident.detail.meta.severity"), trailing: t(`incident.severity.${sev}`) }));
    details.append(hairlineRow({ leading: t("incident.detail.meta.status"), trailing: statusLabel(t, view.status) }));
    if (ctx.weather) {
      details.append(hairlineRow({
        leading: t("incident.detail.meta.wind"),
        trailing: t("incident.detail.wind", {
          speed: String(Math.round(ctx.weather.speedKmh)),
          dir: t(`compass.${compass8(ctx.weather.windFromDeg)}`),
        }),
      }));
    }
    details.append(hairlineRow({
      leading: t("incident.detail.meta.trend"),
      trailing: t(`incident.detail.trend.${trendLabelKey(view)}`),
      tone: view.trend?.worsening ? "accent" : "ink",
    }));
    if (view.lastConfirmedAt) {
      details.append(hairlineRow({ leading: t("incident.detail.meta.lastConfirmed"), trailing: relativeAgo(t, view.lastConfirmedAt, ctx.nowMs) }));
    }
    const sources = contributorLabels(t, view);
    if (sources.length) {
      details.append(hairlineRow({ leading: t("incident.detail.meta.sources"), trailing: sources.join(", ") }));
    }
    // Deep link to the original civil-protection warning(s) — NINA/DWD resolve on
    // warnung.bund.de. Deduped; absent for non-bund (fogos/FIRMS/crowd) contributors.
    const warningUrls = [...new Set((view.contributors ?? [])
      .map((c) => warnungBundUrl(c.feed, c.itemId))
      .filter((u): u is string => u !== null))];
    for (const url of warningUrls) {
      const row = document.createElement("div");
      row.className = "detail-meta__row incident-detail__source-link";
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.className = "bx-body";
      a.textContent = t("now.warning.viewSource");
      row.append(a);
      details.append(row);
    }
    pane.append(details);
    const tech = technicalRows(t, view);
    if (tech.length > 2) {
      const body = document.createElement("div");
      body.className = "detail-meta";
      for (const r of tech) body.append(hairlineRow({ leading: r.label, trailing: r.value }));
      pane.append(collapsible(t("incident.detail.adv.title"), body));
    }
    // Underlying raw reports (power-user) — the observations behind this snapshot, tappable.
    if (ctx.reports && ctx.reports.length > 0) {
      const reps = document.createElement("div");
      reps.className = "now-raw-list detail-reports";
      const rh = document.createElement("div");
      rh.className = "bx-microlabel";
      rh.textContent = t("incident.detail.reports.title", { n: ctx.reports.length });
      reps.append(rh);
      for (const r of [...ctx.reports].slice(0, 12)) {
        const row = document.createElement("button");
        row.type = "button";
        row.className = "now-raw-list__row";
        row.setAttribute("data-raw-report", r.id);
        const lead = r.responseType ? `${t(`hazard.${r.hazard}`)} · ${r.responseType}` : t(`hazard.${r.hazard}`);
        row.append(hairlineRow({ icon: hazardIcon(r.hazard), leading: lead, trailing: r.feed ?? "", tone: "ink" }));
        if (ctx.onOpenReport) row.addEventListener("click", () => ctx.onOpenReport!(r.id));
        reps.append(row);
      }
      pane.append(reps);
    }
    return pane;
  };

  const buildActions = (): HTMLElement => {
    const pane = document.createElement("div");
    pane.className = "detail-pane-body";

    // Corroborate: "I see it too" adds my own report to this incident (D2). Only when the
    // caller wired a handler (i.e. the incident has a cell to corroborate at) — otherwise a
    // dead button would claim "recorded" while publishing nothing.
    if (ctx.onSeeItToo) {
      const onSeeItToo = ctx.onSeeItToo;
      const seeIt = button({
        label: t("incident.detail.seeIt"),
        variant: "secondary",
        onClick: () => { void onSeeItToo(); showFootnote(root, t("incident.detail.seeItDone")); },
      });
      seeIt.setAttribute("data-action", "corroborate");
      pane.append(seeIt);
    }

    // Footer: Directions · Share · I can help (GUI §3). Help opens the helper modal (C5).
    const footer = document.createElement("div");
    footer.className = "detail-footer";
    const directions = button({ label: t("incident.detail.directions"), variant: "secondary", onClick: () => {} });
    directions.setAttribute("data-action", "directions");
    const share = button({
      label: t("incident.detail.share"), variant: "secondary",
      onClick: () => { void (navigator as { share?: (d: unknown) => Promise<void> }).share?.({ title: t(`hazard.${view.hazard}`) }); },
    });
    share.setAttribute("data-action", "share");
    const help = button({ label: t("incident.detail.help"), variant: "secondary", onClick: () => {
      if (ctx.onHelp) return ctx.onHelp();
      showFootnote(root, t("incident.detail.helpSoon"));
    } });
    help.setAttribute("data-action", "help");
    footer.append(directions, share, help);
    pane.append(footer);

    const flag = document.createElement("button");
    flag.type = "button";
    flag.className = "detail-flag bx-microlabel";
    flag.setAttribute("data-action", "flag");
    flag.textContent = t("incident.detail.flag");
    flag.addEventListener("click", () => {
      if (ctx.onFlag) return ctx.onFlag(view.subject);
      showFootnote(root, t("incident.detail.flagSoon"));
    });
    pane.append(flag);
    return pane;
  };

  const safetyPane = { id: "safety", label: t("incident.detail.tab.safety"), build: buildSafety };
  const detailsPane = { id: "details", label: t("incident.detail.tab.details"), build: buildDetails };
  const actionsPane = { id: "actions", label: t("incident.detail.tab.actions"), build: buildActions };
  // In zone: Safety FIRST (panic-ergonomics). Out to 2× radius: lead with Details but keep
  // Safety. Beyond 2× radius: drop Safety entirely.
  const panes = inZone
    ? [safetyPane, detailsPane, actionsPane]
    : showSafety
      ? [detailsPane, safetyPane, actionsPane]
      : [detailsPane, actionsPane];
  body.append(detailCarousel(panes));

  root.append(body);
  return root;
}

/** Swipe/tab carousel for the incident detail. Panels lay out side-by-side in a
 *  horizontal scroll-snap track (native swipe); the tab row jumps to a panel and the
 *  active tab follows the swipe. Defaults to panel 0 (Safety). jsdom-safe (guards
 *  scrollTo / requestAnimationFrame, which jsdom omits). */
function detailCarousel(panes: Array<{ id: string; label: string; build: () => HTMLElement }>): HTMLElement {
  const carousel = document.createElement("div");
  carousel.className = "detail-carousel";

  const tabs = document.createElement("div");
  tabs.className = "detail-carousel__tabs";
  tabs.setAttribute("role", "tablist");

  const track = document.createElement("div");
  track.className = "detail-carousel__track";
  track.setAttribute("data-carousel-track", "");

  const tabButtons: HTMLButtonElement[] = [];
  const setActive = (i: number): void => {
    tabButtons.forEach((b, j) => {
      const on = j === i;
      b.classList.toggle("detail-carousel__tab--active", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    });
  };

  panes.forEach((p, i) => {
    const tab = document.createElement("button");
    tab.type = "button";
    tab.className = "detail-carousel__tab";
    tab.setAttribute("role", "tab");
    tab.setAttribute("data-tab", p.id);
    tab.textContent = p.label;
    tab.addEventListener("click", () => {
      if (typeof track.scrollTo === "function") track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
      setActive(i);
    });
    tabs.append(tab);
    tabButtons.push(tab);

    const panel = document.createElement("section");
    panel.className = "detail-carousel__panel";
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("data-panel", p.id);
    panel.append(p.build());
    track.append(panel);
  });

  setActive(0);
  // Reflect the swipe position back into the tab row (scroll-snap), rAF-throttled.
  let ticking = false;
  track.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    const raf = typeof requestAnimationFrame === "function"
      ? requestAnimationFrame
      : (cb: FrameRequestCallback): number => setTimeout(() => cb(0), 16) as unknown as number;
    raf(() => {
      ticking = false;
      const w = Math.max(1, track.clientWidth);
      setActive(Math.round(track.scrollLeft / w));
    });
  });

  carousel.append(tabs, track);
  return carousel;
}

function showFootnote(root: HTMLElement, text: string): void {
  let note = root.querySelector<HTMLElement>(".detail-footnote");
  if (!note) {
    note = document.createElement("p");
    note.className = "detail-footnote bx-body";
    root.append(note);
  }
  note.textContent = text;
}
